import { ChatBubbleLeftIcon, ClockIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { useRef, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useAction, useActivity, useComments } from "@/lib/queries";
import type { Activity, Comment, Member } from "@/lib/types";
import { cn, formatDateTime, timeAgo } from "@/lib/utils";
import { Tabs } from "../page";
import { Avatar, Button, Spinner } from "../ui";
import { useFeedback } from "../ui/feedback";
import { MenuItem, Popover } from "../ui/Popover";

const MENTION_RE = /@\[([^\]\n]{1,100})\]\(([0-9a-fA-F-]{36})\)/g;
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function linkify(text: string, keyPrefix: string): ReactNode[] {
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a key={`${keyPrefix}-${i}`} href={part} target="_blank" rel="noreferrer" className="text-accent underline-offset-2 hover:underline">
        {part}
      </a>
    ) : (
      part
    ),
  );
}

export function RichComment({ body }: { body: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION_RE)) {
    out.push(...linkify(body.slice(last, m.index), `t${last}`));
    out.push(
      <span key={`m${m.index}`} className="rounded bg-accent/15 px-0.5 font-medium text-accent">
        @{m[1]}
      </span>,
    );
    last = (m.index ?? 0) + m[0].length;
  }
  out.push(...linkify(body.slice(last), `t${last}`));
  return (
    <div className="text-sm leading-relaxed break-words whitespace-pre-wrap" dir="auto">
      {out}
    </div>
  );
}

/** Textarea with @mention autocomplete. Mentions are serialized as @[Name](id) on submit. */
function Composer({
  initial = "",
  onSubmit,
  onCancel,
  submitLabel = "Comment",
  autoFocus,
}: {
  initial?: string;
  onSubmit: (body: string) => Promise<unknown>;
  onCancel?: () => void;
  submitLabel?: string;
  autoFocus?: boolean;
}) {
  const { members, me } = useProject();
  // decode existing tokens into plain "@Name" text plus a mention list
  const [text, setText] = useState(() => initial.replace(MENTION_RE, "@$1"));
  const [mentions, setMentions] = useState<{ id: string; name: string }[]>(() =>
    [...initial.matchAll(MENTION_RE)].map((m) => ({ name: m[1], id: m[2] })),
  );
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  const suggestions: Member[] =
    query === null ? [] : members.filter((m) => m.isActive && m.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6);

  const detect = (value: string, caret: number) => {
    const m = /(^|\s)@([^\s@]{0,30})$/.exec(value.slice(0, caret));
    setQuery(m ? m[2] : null);
    setActive(0);
  };

  const pick = (member: Member) => {
    const el = ref.current!;
    const caret = el.selectionStart;
    const before = text.slice(0, caret).replace(/@([^\s@]{0,30})$/, `@${member.name} `);
    const next = before + text.slice(caret);
    setText(next);
    setMentions((ms) => (ms.some((x) => x.id === member.id) ? ms : [...ms, { id: member.id, name: member.name }]));
    setQuery(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  };

  const serialize = () => {
    let body = text;
    for (const m of [...mentions].sort((a, b) => b.name.length - a.name.length)) {
      body = body.split(`@${m.name}`).join(`@[${m.name}](${m.id})`);
    }
    return body.trim();
  };

  const submit = async () => {
    const body = serialize();
    if (!body || busy) return;
    setBusy(true);
    try {
      await onSubmit(body);
      setText("");
      setMentions([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-2.5">
      <Avatar id={me.id} name={me.name} size={26} className="mt-1" />
      <div className="relative min-w-0 flex-1">
        <textarea
          ref={ref}
          autoFocus={autoFocus}
          value={text}
          rows={Math.min(8, Math.max(2, text.split("\n").length))}
          dir="auto"
          placeholder="Add a comment… Use @ to mention someone"
          onChange={(e) => {
            setText(e.target.value);
            detect(e.target.value, e.target.selectionStart);
          }}
          onClick={(e) => detect(text, (e.target as HTMLTextAreaElement).selectionStart)}
          onKeyDown={(e) => {
            if (suggestions.length) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => (a + 1) % suggestions.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                pick(suggestions[active]);
                return;
              }
              if (e.key === "Escape") {
                e.stopPropagation();
                setQuery(null);
                return;
              }
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          className="field resize-none"
        />
        {suggestions.length > 0 && (
          <div className="absolute start-0 top-full z-20 mt-1 w-64 rounded-lg bg-elevated p-1 shadow-pop">
            {suggestions.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(m);
                }}
                className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm", i === active && "bg-hover")}
              >
                <Avatar id={m.id} name={m.name} size={20} />
                <span className="truncate" dir="auto">
                  {m.name}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-end gap-2">
          <span className="me-auto text-xs text-faint">Ctrl + Enter to send</span>
          {onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button size="sm" variant="primary" onClick={submit} disabled={!text.trim()} loading={busy}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentItem({ comment }: { comment: Comment }) {
  const { pid, me, isOwner, userName } = useProject();
  const { confirm } = useFeedback();
  const [editing, setEditing] = useState(false);
  const update = useAction((body: string) => api.updateComment(pid, comment.id, body), {
    invalidate: [keys.comments(pid, comment.entityId)],
    onSuccess: () => setEditing(false),
  });
  const remove = useAction(() => api.deleteComment(pid, comment.id), { invalidate: [keys.comments(pid, comment.entityId)] });
  const mine = comment.authorId === me.id;
  const name = userName(comment.authorId);

  if (editing) return <Composer initial={comment.body} submitLabel="Save" onSubmit={(b) => update.mutateAsync(b)} onCancel={() => setEditing(false)} autoFocus />;

  return (
    <div className="group flex gap-2.5">
      <Avatar id={comment.authorId ?? "x"} name={name} size={26} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium" dir="auto">
            {name}
          </span>
          <span className="text-xs text-faint" title={formatDateTime(comment.createdAt)}>
            {timeAgo(comment.createdAt)}
            {comment.updatedAt !== comment.createdAt && " (edited)"}
          </span>
          {(mine || isOwner) && (
            <Popover
              placement="bottom-end"
              className="w-36 p-1"
              trigger={(t) => (
                <button ref={t.ref} onClick={t.onClick} className="ms-auto rounded p-0.5 text-faint opacity-0 group-hover:opacity-100 hover:bg-hover">
                  <EllipsisHorizontalIcon className="size-4" />
                </button>
              )}
            >
              {(close) => (
                <>
                  {mine && <MenuItem onClick={() => (close(), setEditing(true))}>Edit</MenuItem>}
                  <MenuItem
                    danger
                    onClick={async () => {
                      close();
                      if (await confirm({ title: "Delete this comment?", confirmLabel: "Delete", danger: true })) remove.mutate();
                    }}
                  >
                    Delete
                  </MenuItem>
                </>
              )}
            </Popover>
          )}
        </div>
        <RichComment body={comment.body} />
      </div>
    </div>
  );
}

export function Comments({ entityId }: { entityId: string }) {
  const { pid } = useProject();
  const comments = useComments(pid, entityId);
  const create = useAction((body: string) => api.createComment(pid, entityId, body), {
    invalidate: [keys.comments(pid, entityId), keys.activity(pid, entityId)],
  });
  return (
    <div className="space-y-5">
      {comments.isPending && <Spinner className="size-4" />}
      {comments.data?.map((c) => <CommentItem key={c.id} comment={c} />)}
      <Composer onSubmit={(b) => create.mutateAsync(b)} />
    </div>
  );
}

function Quote({ children }: { children: ReactNode }) {
  return (
    <span className="font-medium text-fg" dir="auto">
      {children}
    </span>
  );
}

export function describeActivity(a: Activity, actorName: string): ReactNode {
  const d = a.data as Record<string, string & string[]>;
  const list = (v: unknown) => (Array.isArray(v) ? v.join(", ") : String(v ?? ""));
  switch (a.action) {
    case "created":
      return <>created this {a.entityType}</>;
    case "renamed":
      return (
        <>
          renamed it to <Quote>{d.to}</Quote>
        </>
      );
    case "body_edited":
      return <>edited the description</>;
    case "property_changed":
      return d.to?.length ? (
        <>
          set <Quote>{d.property}</Quote> to <Quote>{list(d.to)}</Quote>
        </>
      ) : (
        <>
          cleared <Quote>{d.property}</Quote>
        </>
      );
    case "field_changed":
      return d.to ? (
        <>
          changed <Quote>{d.field}</Quote> to <Quote>{d.to}</Quote>
        </>
      ) : (
        <>
          cleared <Quote>{d.field}</Quote>
        </>
      );
    case "stage_changed":
      return (
        <>
          moved it from <Quote>{d.from}</Quote> to <Quote>{d.to}</Quote>
        </>
      );
    case "sprint_changed":
      return (
        <>
          moved it from <Quote>{d.from}</Quote> to <Quote>{d.to}</Quote>
        </>
      );
    case "epic_changed":
      return (
        <>
          moved it to the epic <Quote>{d.to}</Quote>
        </>
      );
    case "assigned":
      return d.user === actorName ? (
        <>assigned themselves</>
      ) : (
        <>
          assigned <Quote>{d.user}</Quote>
        </>
      );
    case "unassigned":
      return d.user === actorName ? (
        <>unassigned themselves</>
      ) : (
        <>
          unassigned <Quote>{d.user}</Quote>
        </>
      );
    case "assignee_status_changed":
      return d.status ? (
        <>
          set <Quote>{d.user}</Quote>'s status to <Quote>{d.status}</Quote>
        </>
      ) : (
        <>
          cleared <Quote>{d.user}</Quote>'s status
        </>
      );
    case "pr_link_added":
      return (
        <>
          added a PR link{" "}
          <a href={d.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
            {d.url}
          </a>
        </>
      );
    case "commented":
      return (
        <>
          commented <span className="text-muted">“{d.snippet}”</span>
        </>
      );
    case "file_linked":
      return (
        <>
          attached <Quote>{d.file}</Quote>
        </>
      );
    default:
      return <>{a.action.replaceAll("_", " ")}</>;
  }
}

export function ActivityList({ entityId }: { entityId: string }) {
  const { pid, userName } = useProject();
  const activity = useActivity(pid, entityId);
  if (activity.isPending) return <Spinner className="size-4" />;
  return (
    <ol className="relative space-y-3 border-s border-line ps-4">
      {activity.data?.map((a) => {
        const actor = userName(a.actorId);
        return (
          <li key={a.id} className="relative text-sm">
            <span className="absolute top-1.5 -start-[21px] size-2 rounded-full bg-faint" />
            <span className="font-medium" dir="auto">
              {actor}
            </span>{" "}
            <span className="text-muted">{describeActivity(a, actor)}</span>
            <span className="ms-2 text-xs text-faint" title={formatDateTime(a.createdAt)}>
              {timeAgo(a.createdAt)}
            </span>
          </li>
        );
      })}
      {activity.data?.length === 0 && <li className="text-sm text-muted">No activity yet</li>}
    </ol>
  );
}

export function Discussion({ entityId }: { entityId: string }) {
  const [tab, setTab] = useState<"comments" | "activity">("comments");
  return (
    <div className="space-y-4">
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "comments", label: "Comments", icon: ChatBubbleLeftIcon },
          { id: "activity", label: "History", icon: ClockIcon },
        ]}
      />
      {tab === "comments" ? <Comments entityId={entityId} /> : <ActivityList entityId={entityId} />}
    </div>
  );
}

